import { useState } from 'react';
import { Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import retireWorker from 'store/retireWorker';

interface RetireWorkerDialogProps {
  workerId: string;
  workerName: string;
  packageName?: string;
  packageVersion?: string;
  onClose: () => void;
  onConfirm: () => void;
}

export default function RetireWorkerDialog({
  workerId,
  workerName,
  packageName,
  packageVersion,
  onClose,
  onConfirm,
}: RetireWorkerDialogProps) {
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await retireWorker(workerId);
      onConfirm();
      onClose();
    } catch (err) {
      console.error('Failed to retire worker:', err);
      setError('Failed to retire worker. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setError(null);
    onClose();
  };

  return (
    <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Retire Worker</DialogTitle>
      <DialogContent>
        <DialogContentText>
          This will retire worker <strong>{workerName}</strong>
          {packageName && (
            <>
              {' '}
              running package <strong>{packageName}</strong>
              {packageVersion && (
                <>
                  {' '}
                  <strong>{packageVersion}</strong>
                </>
              )}
            </>
          )}
          . The worker will stop picking up new tasks but will finish any currently running tasks before stopping.
        </DialogContentText>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleConfirm} variant="contained" color="warning" disabled={isSubmitting}>
          {isSubmitting ? 'Retiring\u2026' : 'Retire'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
